import type {
  CartItem,
  ConfigurationFlowId,
  OrderAiEditingConfigurationDTO,
  OrderAuthoritiesDocumentConfigurationDTO,
  OrderCertificateOfNeedConfigurationDTO,
  OrderConsumptionCertificateConfigurationDTO,
  OrderFloorPlanCertificationConfigurationDTO,
  OrderFloorPlanConfigurationDTO,
  OrderFloorPlanProductConfigDTO,
  OrderMeasurementOnSiteConfigurationDTO,
  OrderStagingConfigurationDTO,
  OrderStagingDetailsDTO
} from 'models/purchaseFlowRevamp'
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable'
import { getAPIRoomItemFromStagingRoom } from 'utils/serialization/getAPIRoomItemFromStagingRoom'

import { MEASUREMENT_DEFAULT, toMeasurementRequest } from '../modules/measurement/measurement.dto'
import type { MeasurementDraftDTO } from '../modules/measurement/measurement.dto'
import { toAuthoritiesDocumentRequest } from '../modules/propertyInfo/propertyInfo.dto'
import type { PropertyInfoDraftDTO } from '../modules/propertyInfo/propertyInfo.dto'
import { ROOM_DEFAULT } from '../modules/staging/staging.dto'
import type { StagingDraftDTO, StagingDraftRoomDTO } from '../modules/staging/staging.dto'
import { SHARED_ANSWER_FLOWS } from '../steps/configuration/resolveTabs'
import { compact } from './wire'

/**
 * The single answer to each order-level flow, taken from wherever it is stored.
 *
 * An order-level configuration is collected ONCE — on the first line that needs
 * it (see `SHARED_ANSWER_FLOWS`) — but belongs to every line of that flow, which
 * would otherwise be placed with no configuration at all: accepted by the wire,
 * undeliverable in practice.
 *
 * Read off the STORED keys rather than re-derived from the catalogue, because
 * the tab list resolves flows behind a feature flag that placement cannot see;
 * taking the data means the line the client filled in and the lines serialized
 * from it cannot disagree.
 */
export const orderLevelConfigs = (
  configs: Record<string, unknown> | undefined
): Record<string, unknown> => {
  const shared: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(configs ?? {})) {
    const flow = key.slice(key.indexOf('|') + 1)
    if (!SHARED_ANSWER_FLOWS.has(flow as never)) continue
    if (!(flow in shared) && isStoredObject(value)) shared[flow] = value
  }
  return shared
}

/** Flows whose answer is collected on another flow's page, keyed by the flow a product announces. */
const COLLECTED_WITH: Readonly<Partial<Record<ConfigurationFlowId, ConfigurationFlowId[]>>> = {
  power_of_attorney: ['property_info'],
}

/**
 * Whether a line whose product announces `lineFlows` takes the shared answer to `flow`. The backend
 * rejects the whole order when any line carries a configuration its product does not accept, so a
 * shared answer never reaches a line of another flow.
 */
const usesFlow = (lineFlows: ReadonlyArray<ConfigurationFlowId>, flow: string): boolean =>
  lineFlows.some((own) => own === flow || (COLLECTED_WITH[own] ?? []).includes(flow as ConfigurationFlowId))

/**
 * Per-product configuration for a line, or `undefined` when it has none (the field is optional on
 * the wire). A line's own stored answer wins; a shared flow falls back to the single answer given
 * on whichever line collected it, but only when the line's product uses that flow.
 */
export function buildConfiguration(
  item: CartItem,
  configs: Record<string, unknown> | undefined,
  /** The order-level answers, shared across the lines that need them. */
  shared: Record<string, unknown>,
  /** The flows this line's product announces. */
  lineFlows: ReadonlyArray<ConfigurationFlowId>,
  /** Restricts which stored answers are read: an add-on reads only its own flows, the line skips the
   *  flows only its add-ons announce (they share the line's config keys). */
  scope: { only?: ReadonlyArray<ConfigurationFlowId>; exclude?: ReadonlyArray<ConfigurationFlowId> } = {}
): OrderAiEditingConfigurationDTO
  | OrderFloorPlanConfigurationDTO
  | OrderStagingConfigurationDTO
  | OrderConsumptionCertificateConfigurationDTO
  | OrderCertificateOfNeedConfigurationDTO
  | OrderAuthoritiesDocumentConfigurationDTO
  | OrderMeasurementOnSiteConfigurationDTO
  | OrderFloorPlanCertificationConfigurationDTO
  | undefined {
  const inScope = (flow: string): boolean =>
    (!scope.only || scope.only.includes(flow as ConfigurationFlowId))
    && !scope.exclude?.includes(flow as ConfigurationFlowId)
  const cfg = (flow: string): unknown => {
    if (!inScope(flow)) return undefined
    return configs?.[`${item.id}|${flow}`] ?? (usesFlow(lineFlows, flow) ? shared[flow] : undefined)
  }

  if (item.aiEditing && !scope.only) {
    return {
      type: 'AI_EDITING',
      details: {
        skyReplacement: item.aiEditing.skyReplacement,
        // Only meaningful alongside an enabled replacement. The slice clears it when
        // the switch goes off; this keeps a hand-built item honest too.
        cloudType: item.aiEditing.skyReplacement ? item.aiEditing.cloudType ?? null : null,
      },
    }
  }

  const staging = buildStagingConfiguration(cfg('staging'))
  if (staging) return staging

  const consumption = cfg('consumption_certificate')
  if (isStoredObject(consumption)) {
    return { type: 'CONSUMPTION_CERTIFICATE', details: stripDraftOnlyCertificateFields(consumption) }
  }

  const certificateOfNeed = cfg('energy_certificate_of_need')
  if (isStoredObject(certificateOfNeed)) {
    return { type: 'CERTIFICATE_OF_NEED', details: stripDraftOnlyCertificateFields(certificateOfNeed) }
  }

  const certification = cfg('floor_plan_certification')
  if (isStoredObject(certification)) {
    // `numberOfFloors` only decides how many floor rows the form renders; the
    // wire counts `floors` itself.
    const { numberOfFloors: _rowCount, ...request } = certification
    return { type: 'FLOOR_PLAN_CERTIFICATION', request }
  }

  const measurement = cfg('measurement')
  if (isStoredObject(measurement)) {
    // `toMeasurementRequest` owns the redesign's three flattening rules, so the
    // quote the client was shown and the order that is billed are built from one
    // place and cannot disagree.
    return {
      type: 'MEASUREMENT_ON_SITE',
      request: toMeasurementRequest({
        ...MEASUREMENT_DEFAULT,
        ...(measurement as Partial<MeasurementDraftDTO>),
      }) as unknown as Record<string, unknown>,
    }
  }

  const propertyInfo = cfg('property_info')
  if (isStoredObject(propertyInfo)) {
    return { type: 'AUTHORITIES_DOCUMENT', request: buildAuthoritiesDocumentRequest(propertyInfo) }
  }

  return buildFloorPlanConfiguration(cfg('floor_plan_config'))
}

/**
 * The stored property-information draft → its wire `request`.
 *
 * `searchLandRegistrationDetails` records that the client asked us to look the
 * sheets up, which the module already answers by emptying them; the wire has no
 * such field. `toAuthoritiesDocumentRequest` owns that emptying, so the two
 * cannot disagree about what a searched line sends.
 */
function buildAuthoritiesDocumentRequest(stored: Record<string, unknown>): Record<string, unknown> {
  const { searchLandRegistrationDetails: _searchIntent, ...request } =
    toAuthoritiesDocumentRequest(stored as PropertyInfoDraftDTO)

  return request as unknown as Record<string, unknown>
}

const isStoredObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && Object.keys(value).length > 0

/**
 * Fields the certificate modules add to the persisted DTO that the WIRE must not
 * carry.
 *
 * Both certificate drafts are the legacy payload DTO plus a few additive FE-only
 * markers — the module persists the wire shape verbatim, so the projection is a
 * subtraction rather than a translation. The markers exist because the v2 wire
 * carries no filename for an uploaded document, so completeness is gated on the
 * FE remembering what was uploaded; that is a draft concern and the backend has
 * no field for it.
 */
const DRAFT_ONLY_CERTIFICATE_FIELDS = [
  'isConstructionConfirmed',
  'hasVentilationSystem',
  'exteriorViewFilenames',
  'heaterNameplateFilenames',
  'buildingShapeFilenames',
  'constructionDescriptionFilenames',
  'wallDimensionsByShape',
] as const

/**
 * The stored certificate draft → its wire `details`.
 *
 * Listing what to REMOVE rather than what to keep is deliberate: the payload DTO
 * has ~30 fields across heaters, billing periods and vacancy, and an allow-list
 * would silently drop any field the BE adds later. A draft-only marker is the
 * rare, enumerable case — and a stale one left in the list is harmless, while a
 * forgotten payload field is a lost answer the client typed.
 */
function stripDraftOnlyCertificateFields(stored: Record<string, unknown>): Record<string, unknown> {
  const details = { ...stored }
  for (const field of DRAFT_ONLY_CERTIFICATE_FIELDS) delete details[field]
  return details
}

/**
 * The stored staging draft → the `STAGING` wire shape.
 *
 * Legacy already owns this serialization (`getAPIRoomItemFromStagingRoom`, used
 * by `PurchaseFlowPlaceOrder.getStagingConfig`), so this REUSES it rather than
 * restating the room→details rules — the BKBN-vs-CUSTOM split, the
 * renovation-derived `removeObjects` back-compat ladder, the flooring and
 * wall-painting mapping. Re-deriving them here would be a second implementation
 * of a mapping the editors already depend on.
 *
 * Two shape differences the reuse has to bridge, both mechanical:
 *   - the v2 draft stores the immutable collections as plain arrays / `[k, v][]`
 *     pairs, so they are rehydrated before the shared serializer sees them;
 *   - `productId` is STRIPPED. `V2OrderValidator.ensureNoLegacyProductIdentifiers`
 *     rejects a staging config that carries one — the line is the product
 *     and the server stamps the resolved id back itself.
 *
 * Keyed by the room key, which IS the session-scoped image filename
 * (`PfV2StagingVisual.id` = `CartAttachment.gcFilename`) — the same value the
 * line's `inputImages` carry, so details and images line up by construction.
 * Legacy fanned one room config across that room's images; v2 rooms are already
 * one-per-image, so the fan-out has nothing to do.
 */
export function buildStagingConfiguration(stored: unknown): OrderStagingConfigurationDTO | undefined {
  if (!stored || typeof stored !== 'object') return undefined

  const rooms = (stored as StagingDraftDTO).rooms
  if (!rooms || Object.keys(rooms).length === 0) return undefined

  const details: Record<string, OrderStagingDetailsDTO> = {}
  for (const [imageKey, raw] of Object.entries(rooms)) {
    const room = { ...ROOM_DEFAULT, ...raw } as StagingDraftRoomDTO
    const { productId: _legacyProductId, ...detail } = getAPIRoomItemFromStagingRoom({
      ...room,
      roomTypes: ImmutableSet(room.roomTypes ?? []),
      styleCodeMap: ImmutableMap(room.styleCodeMap ?? []),
      decorations: ImmutableSet(room.decorations ?? []),
      renovations: ImmutableMap(room.renovations ?? []),
    })
    details[imageKey] = detail
  }

  return { type: 'STAGING', request: { details } }
}

/**
 * The stored floor-plan config → the `FLOOR_PLAN` wire shape.
 *
 * `FloorPlanConfigDraftDTO` already mirrors legacy's `getFloorPlanRequestData`
 * output (the module writes the wire values, e.g. it zeroes `dimensions` when the
 * group is switched off rather than gating at serialize), so this is a projection
 * rather than a translation: pick the wire subset, leave the FE-only IA fields
 * (`currentFloorPlanRenderType`, `isActive*`, template ids, `selectedPropertyType`)
 * behind, and apply the two rules that are v2-only.
 *
 * 2D vs 3D is NOT a wire field: it rides on `style` (a colour for 2D, a style for
 * 3D) plus `orientation` (3D only), exactly as legacy encoded it.
 */
function buildFloorPlanConfiguration(stored: unknown): OrderFloorPlanConfigurationDTO | undefined {
  if (!stored || typeof stored !== 'object') return undefined
  const c = stored as Record<string, any>

  return compact<OrderFloorPlanConfigurationDTO>({
    type: 'FLOOR_PLAN',
    request: {
      companyLogo: c.companyLogo ?? null,
      style: c.style,
      orientation: c.orientation,
      fileOrientation: c.fileOrientation ?? [],
      fixtures: !!c.fixtures,
      furniture: !!c.furniture,
      roomNames: !!c.roomNames,
      scaleBar: !!c.scaleBar,
      compass: !!c.compass,
      outputFileFormat: c.outputFileFormat ?? [],
      outputFileLanguage: c.outputFileLanguage,
      dimensions: c.dimensions ?? null,
      isMultiStoryPlan: !!c.isMultiStoryPlan,
      // The line IS the product: `V2OrderValidator` rejects a v2
      // config that still carries the legacy id-keyed map, so it goes out empty
      // and its single entry rides on `productConfig` below.
      floorPlanProductConfig: {},
      gardensAndRoofSlopes: !!c.gardensAndRoofSlopes,
      disclaimer: c.disclaimer,
      clientComment: c.clientComment,
    },
    productConfig: buildFloorPlanProductConfig(c.floorPlanProductConfig),
  })
}

/**
 * Lifts the sole per-product entry out of the legacy map. A v2 line carries at
 * most one, so the first entry is it.
 *
 * The comment names the unit to draw when the flag is set, and carries optional
 * notes (e.g. legend info) for the entire plan otherwise (ISSUE-3954), so it
 * ships for both options. A blank comment goes out as `null`.
 */
function buildFloorPlanProductConfig(map: unknown): OrderFloorPlanProductConfigDTO | undefined {
  if (!map || typeof map !== 'object') return undefined
  const [settings] = Object.values(map as Record<string, any>)
  if (!settings) return undefined

  const comment: unknown = settings.drawOnlySpecificUnitComment
  return {
    drawOnlySpecificUnit: !!settings.drawOnlySpecificUnit,
    drawOnlySpecificUnitComment: typeof comment === 'string' && comment.trim().length > 0 ? comment : null,
  }
}
